import { useEffect, useRef } from 'react';
import Draw, { type DrawEvent } from 'ol/interaction/Draw';
import { useMapContext } from '../../map/MapContext';
import type { MeasurementType } from '../../map/measurement';
import { attachDrawOverlays } from './drawOverlays';
import { Tooltip } from '../Tooltip/Tooltip';
import { CircleToolIcon, ClearIcon, LineToolIcon, PointToolIcon, ZoneToolIcon } from '../icons/Icons';
import './measure-toolbar.css';

const TOOLS: Array<{
  id: MeasurementType;
  label: string;
  hint: string;
  drawType: 'Point' | 'LineString' | 'Polygon' | 'Circle';
  Icon: (props: { size?: number }) => JSX.Element;
}> = [
  { id: 'point', label: 'Point', hint: 'Click the map to drop a coordinate marker.', drawType: 'Point', Icon: PointToolIcon },
  {
    id: 'line',
    label: 'Line',
    hint: 'Click to add vertices, double-click to finish — measures distance.',
    drawType: 'LineString',
    Icon: LineToolIcon,
  },
  {
    id: 'zone',
    label: 'Zone',
    hint: 'Click to add vertices, double-click to close — measures area.',
    drawType: 'Polygon',
    Icon: ZoneToolIcon,
  },
  {
    id: 'circle',
    label: 'Circle',
    hint: 'Click a centre and drag to set the radius — measures area.',
    drawType: 'Circle',
    Icon: CircleToolIcon,
  },
];

/**
 * Measurement tool group: Point/Line/Zone/Circle, icon-only buttons with
 * hover-card names/descriptions (Tooltip, unchanged). Each finished shape
 * becomes a persistent, named MeasurementEntity (MapContext.addMeasurement)
 * rather than an ephemeral sketch — see the "Workspace content" Scene
 * Manager group (sceneTree.ts) and MeasurementDetailPanel for where it
 * lives afterward. While actively drawing, live edge-length/area/help
 * labels track the sketch geometry — see drawOverlays.ts.
 */
export function MeasureToolbar() {
  const { map, activeMeasureTool, setActiveMeasureTool, measurementsSource, addMeasurement, clearMeasurements } =
    useMapContext();
  const drawRef = useRef<Draw | null>(null);

  useEffect(() => {
    if (drawRef.current) {
      map.removeInteraction(drawRef.current);
      drawRef.current = null;
    }
    if (!activeMeasureTool) return;
    const tool = TOOLS.find((t) => t.id === activeMeasureTool)!;
    const draw = new Draw({
      source: measurementsSource,
      type: tool.drawType,
    });
    const detachOverlays = attachDrawOverlays(map, draw, tool.id);

    // Tracked locally (not React state — this only needs to be read
    // synchronously inside the keydown handler below, a render for every
    // vertex placed would be wasteful) so Escape only cancels a sketch
    // that's actually in progress, rather than swallowing every Escape
    // press whenever a tool happens to be armed.
    let sketchInProgress = false;
    draw.on('drawstart', () => {
      sketchInProgress = true;
    });
    draw.on('drawend', (evt: DrawEvent) => {
      sketchInProgress = false;
      addMeasurement(evt.feature, tool.id);
    });
    draw.on('drawabort', () => {
      sketchInProgress = false;
    });

    // Escape has two different jobs depending on whether a sketch is
    // actually in progress:
    //  - mid-sketch: cancel just the shape (removes the partial geometry
    //    and its live overlays via drawOverlays.ts's own 'drawabort'
    //    handler, below) and keep the tool armed — same "cancel the shape,
    //    keep the tool so the user can just click and start again" pattern
    //    as Illustrator/Figma's escape-cancel, and OL's own abortDrawing()
    //    convention.
    //  - idle (tool armed, nothing placed yet): there's nothing to abort,
    //    so instead deactivate the tool entirely and fall back to the
    //    default pointer/no-tool state — same as clicking the already-
    //    active toolbar button again.
    // Registered on the CAPTURE phase and stops the event entirely in
    // either case, so this takes priority over (and suppresses) any other
    // Escape handler that might also be listening at this moment — e.g. a
    // detail panel's own "More" dropdown, which must not also react to the
    // same keypress while a measure tool is armed.
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (sketchInProgress) {
        draw.abortDrawing();
      } else {
        setActiveMeasureTool(null);
      }
    }
    document.addEventListener('keydown', onKeyDown, true);

    map.addInteraction(draw);
    drawRef.current = draw;
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      detachOverlays();
      map.removeInteraction(draw);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMeasureTool, map]);

  return (
    <div className="measure-toolbar-group">
      {TOOLS.map((tool) => (
        <Tooltip key={tool.id} heading={tool.label} description={tool.hint}>
          <button
            type="button"
            className={`measure-btn${activeMeasureTool === tool.id ? ' active' : ''}`}
            aria-label={tool.label}
            aria-pressed={activeMeasureTool === tool.id}
            onClick={() => setActiveMeasureTool(activeMeasureTool === tool.id ? null : tool.id)}
          >
            <tool.Icon size={16} />
          </button>
        </Tooltip>
      ))}
      <Tooltip heading="Clear" description="Remove every measurement from the map.">
        <button type="button" className="measure-btn measure-clear" aria-label="Clear all measurements" onClick={clearMeasurements}>
          <ClearIcon size={15} />
        </button>
      </Tooltip>
    </div>
  );
}
